import React, { useEffect, useState, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import { IScore, IBarnHuntScore } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch from "src/components/EnterResults/StopWatch/StopWatch";

import "./BarnHuntResults.scss";

interface IProps {
  result: IScore;
  onChange(result: IBarnHuntScore): void;
}

/**
 * Standard Class Results Form (Novice, Open, Senior, Master)
 * - Pass/Fail/Absent toggle
 * - Required time in MM:SS.00 format (if Pass)
 * - Placements calculated by backend based on section (A/B) and height
 * - Master B: REMX flag if time <= 2:30
 */
const StandardClassForm: React.FC<IProps> = (props) => {
  const [time, setTime] = useState<string>("0:00:00");
  const [result, setResult] = useState<IOption>({ id: Constants.score_result_type.qualified, value: "Qualified" });
  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const level = props.result.dog_class?.level;
  const section = props.result.dog_class?.section;

  const resultOptions: IOption[] = useMemo(
    () => [
      { id: Constants.score_result_type.qualified, value: "Pass/Q" },
      { id: Constants.score_result_type.not_qualified, value: "Fail/NQ" },
      { id: Constants.score_result_type.absent, value: "Absent" },
    ],
    []
  );

  useEffect(() => {
    if (!loaded) {
      if (props.result && props.result.barn_hunt) {
        const { result_type, time: savedTime } = props.result.barn_hunt;
        for (let ro in resultOptions) {
          if (resultOptions[ro].id === result_type) {
            setResult(resultOptions[ro]);
            break;
          }
        }

        if (savedTime) {
          // Stored as MM:SS.TT, but StopWatch expects MM:SS:TT
          setTime(savedTime.replace(".", ":"));
        }
      }
      setLoaded(true);
    }
  }, [loaded, props.result, resultOptions]);

  useEffect(() => {
    if (hasChanged) {
      // Calculate REMX for Master B if applicable
      const isRemx =
        level === Constants.dog_class_element_level.barn_hunt_master &&
        section === "B" &&
        result.id === Constants.score_result_type.qualified &&
        time
          ? isTimeUnderRemxLimit(time)
          : false;

      // Convert from M:SS:TT to MM:SS.TT format for storage
      let resultTime: string | undefined;
      const parts = time.split(":");
      if (parts.length === 3) {
        resultTime = `${parts[0]}:${parts[1]}.${parts[2]}`;
      }

      props.onChange({
        result_type: result.id,
        time: resultTime,
        remx_flag: isRemx || undefined,
      });

      setHasChanged(false);
    }
  }, [hasChanged, time, props, result.id, level, section]);

  const handleTimeChange = (value: string) => {
    setTime(value);
    setHasChanged(true);
  };

  const handleResultChange = (e: any, value: IOption) => {
    setResult(value);
    setHasChanged(true);
  };

  // Check if time is under REMX limit (2:30)
  const isTimeUnderRemxLimit = (timeStr: string): boolean => {
    const parts = timeStr.split(":");
    if (parts.length !== 3) return false;

    const minutes = parseInt(parts[0], 10);
    const seconds = parseFloat(parts[1]);

    if (isNaN(minutes) || isNaN(seconds)) return false;

    const totalSeconds = minutes * 60 + seconds;
    return totalSeconds <= 150; // 2:30
  };

  // Display information about the class
  const placementInfo = useMemo(() => {
    if (section === "A") {
      return "A-level: Placements by height division (S/M/L)";
    } else if (section === "B") {
      return "B-level: Placements across all heights";
    }
    return "";
  }, [section]);

  // Display REMX eligibility for Master B
  const showRemxInfo =
    level === Constants.dog_class_element_level.barn_hunt_master &&
    section === "B" &&
    result.id === Constants.score_result_type.qualified;

  const isRemxEligible = showRemxInfo && time ? isTimeUnderRemxLimit(time) : false;

  return (
    <div className="BarnHuntResults">
      <div className="instruction-section">
        <p className="instruction-text">
          <strong>{level} {section}:</strong> Enter Pass/Fail and time (MM:SS.00).
          {placementInfo && <><br />{placementInfo}</>}
        </p>
      </div>

      <div className="form-row">
        <Dropdown
          value={result}
          label="Result"
          onChange={handleResultChange}
          options={resultOptions}
          placeholder=""
        />
      </div>

      {result.id === Constants.score_result_type.qualified && (
        <div className="form-row">
          <StopWatch
            time={time}
            onChange={handleTimeChange}
            label="Time"
          />
        </div>
      )}

      {showRemxInfo && time && (
        <div className="result-display barn-hunt-remx-banner">
          <div className="display-item">
            <label>REMX Eligible:</label>
            <div className="display-value">{isRemxEligible ? "Yes (≤ 2:30)" : "No"}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardClassForm;

